import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { SHARED_LINK_QUERY } from '../../graphql/queries';
import styles from './DWGViewer.module.css';

const TOKEN_KEY = 'geodraft_token';

const FILE_QUERY = gql`
  query BIMFile($id: ID!) {
    file(id: $id) {
      id
      originalName
      fileSize
      apsUrn
      apsStatus
    }
  }
`;

// ── APS SDK loader ─────────────────────────────────────────────────────────────

const APS_VIEWER_VERSION = '7.*';
const APS_CSS = `https://developer.api.autodesk.com/modelderivative/v2/viewers/${APS_VIEWER_VERSION}/style.min.css`;
const APS_JS  = `https://developer.api.autodesk.com/modelderivative/v2/viewers/${APS_VIEWER_VERSION}/viewer3D.min.js`;

let sdkLoaded = false;
let sdkLoading: Promise<void> | null = null;
let viewerInitialized = false;
let viewerInitializing: Promise<void> | null = null;

function loadSdk(): Promise<void> {
  if (sdkLoaded) return Promise.resolve();
  if (sdkLoading) return sdkLoading;

  sdkLoading = new Promise<void>((resolve, reject) => {
    if (!document.querySelector(`link[href="${APS_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = APS_CSS;
      document.head.appendChild(link);
    }
    const script = document.createElement('script');
    script.src = APS_JS;
    script.onload = () => { sdkLoaded = true; resolve(); };
    script.onerror = () => reject(new Error('Failed to load Autodesk Viewer SDK'));
    document.head.appendChild(script);
  });

  return sdkLoading;
}

function initializeViewerRuntime(options: Autodesk.Viewing.InitializerOptions): Promise<void> {
  if (viewerInitialized) return Promise.resolve();
  if (viewerInitializing) return viewerInitializing;

  viewerInitializing = new Promise<void>((resolve) => {
    Autodesk.Viewing.Initializer(options, () => {
      viewerInitialized = true;
      resolve();
    });
  });

  return viewerInitializing;
}

// ── Types ─────────────────────────────────────────────────────────────────────

type Stage =
  | 'idle'
  | 'uploading'
  | 'inprogress'
  | 'loading'
  | 'ready'
  | 'failed'
  | 'misconfigured';

// ── Component ─────────────────────────────────────────────────────────────────

const BIMViewer = () => {
  // Mounted on two routes: /projects/:projectId/viewer-3d/:fileId (authenticated)
  // and /share/:token/viewer-3d/:fileId (public share link, no account).
  const { fileId, token: shareToken } = useParams<{ projectId?: string; fileId: string; token?: string }>();
  const isShare = !!shareToken;
  const shareQS = isShare ? `?shareToken=${encodeURIComponent(shareToken!)}` : '';
  const navigate = useNavigate();

  const viewerContainerRef = useRef<HTMLDivElement>(null);
  const viewerInstanceRef = useRef<Autodesk.Viewing.GuiViewer3D | null>(null);

  const [stage, setStage] = useState<Stage>('idle');
  const [progress, setProgress] = useState('0%');
  const [error, setError] = useState('');
  const [urn, setUrn] = useState('');

  const authHeader = useCallback(() => {
    const token = localStorage.getItem(TOKEN_KEY) || '';
    return { Authorization: `Bearer ${token}` };
  }, []);

  const { data, refetch } = useQuery(FILE_QUERY, {
    variables: { id: fileId },
    skip: !fileId || isShare,
  });

  // Share visitors have no account — file info comes from the public share-link
  // query instead (it already returns apsUrn/apsStatus per file).
  const { data: shareData } = useQuery(SHARED_LINK_QUERY, {
    variables: { token: shareToken },
    skip: !isShare,
    fetchPolicy: 'no-cache',
  });

  const fileInfo = isShare
    ? shareData?.sharedLink?.files?.find((f: { id: string }) => f.id === fileId)
    : data?.file;

  useEffect(() => {
    if (!fileInfo) return;
    if (fileInfo.apsStatus === 'success' && fileInfo.apsUrn) {
      setUrn(fileInfo.apsUrn);
      setStage('loading');
    } else if (fileInfo.apsStatus === 'inprogress') {
      setUrn(fileInfo.apsUrn ?? '');
      setStage('inprogress');
    } else if (fileInfo.apsStatus === 'failed') {
      setStage('failed');
      setError('Previous translation failed. Try again.');
    } else {
      setStage('idle');
    }
  }, [fileInfo]);

  useEffect(() => {
    if (stage !== 'inprogress') return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/api/aps/status/${fileId}${shareQS}`, isShare ? undefined : { headers: authHeader() });
        const data = await res.json() as { status: string; progress: string; urn: string };
        setProgress(data.progress ?? '0%');
        if (data.status === 'success') {
          clearInterval(interval);
          setUrn(data.urn);
          setStage('loading');
          if (!isShare) refetch();
        } else if (data.status === 'failed' || data.status === 'timeout') {
          clearInterval(interval);
          setStage('failed');
          setError('Translation failed on the Autodesk server.');
        }
      } catch {
        // network hiccup — keep polling
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [stage, fileId, authHeader, refetch, isShare, shareQS]);

  useEffect(() => {
    return () => {
      if (viewerInstanceRef.current) {
        viewerInstanceRef.current.finish();
        viewerInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (stage !== 'loading' || !urn || !viewerContainerRef.current) return;

    let viewer: Autodesk.Viewing.GuiViewer3D | null = null;
    let destroyed = false;
    let reachedReady = false;

    const timeout = setTimeout(() => {
      if (!destroyed) {
        destroyed = true;
        if (viewerInstanceRef.current) {
          viewerInstanceRef.current.finish();
          viewerInstanceRef.current = null;
        }
        setStage('failed');
        setError('Viewer timed out. The translation may be stale — try retranslating the file.');
      }
    }, 45_000);

    const init = async () => {
      try {
        await loadSdk();
        if (destroyed) return;

        const tokenRes = await fetch(`/api/aps/token${shareQS}`, isShare ? undefined : { headers: authHeader() });
        if (!tokenRes.ok) {
          const body = await tokenRes.json() as { error: string };
          if (tokenRes.status === 500 && body.error?.includes('not configured')) {
            setStage('misconfigured');
            return;
          }
          throw new Error(body.error ?? 'Token fetch failed');
        }
        const { access_token, expires_in } = await tokenRes.json() as { access_token: string; expires_in: number };
        if (destroyed) return;

        await initializeViewerRuntime({
          env: 'AutodeskProduction2',
          api: 'streamingV2',
          getAccessToken: (cb) => cb(access_token, expires_in),
        });
        if (destroyed || !viewerContainerRef.current) return;

        if (viewerInstanceRef.current) {
          viewerInstanceRef.current.finish();
          viewerInstanceRef.current = null;
        }

        viewer = new Autodesk.Viewing.GuiViewer3D(viewerContainerRef.current);
        viewer.start();
        viewerInstanceRef.current = viewer;

        await new Promise<void>((resolve, reject) => {
          Autodesk.Viewing.Document.load(
            `urn:${urn}`,
            (doc) => {
              if (destroyed) { resolve(); return; }
              const root = doc.getRoot();
              // RVT files are 3D — prefer 3D viewable, fall back to 2D or default
              const viewables3d = root.search({ type: 'geometry', role: '3d' });
              const viewables2d = root.search({ type: 'geometry', role: '2d' });
              console.log('[BIMViewer] viewables 3d:', viewables3d.length, '2d:', viewables2d.length);
              const viewable = viewables3d[0] ?? viewables2d[0] ?? root.getDefaultGeometry(true);
              if (!viewable) {
                reject(new Error('No viewable geometry found in this document.'));
                return;
              }
              viewer!.loadDocumentNode(doc, viewable)
                .then(() => {
                  viewer!.resize();
                  const onGeomLoaded = () => {
                    viewer!.fitToView();
                    viewer!.removeEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, onGeomLoaded);
                  };
                  viewer!.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, onGeomLoaded);
                  setTimeout(() => { if (!destroyed) viewer!.fitToView(); }, 1500);
                  resolve();
                })
                .catch((err: unknown) => reject(err instanceof Error ? err : new Error(String(err))));
            },
            (_code, message) => reject(new Error(message ?? 'Document load error')),
          );
        });

        if (!destroyed) {
          clearTimeout(timeout);
          reachedReady = true;
          setStage('ready');
        }
      } catch (err) {
        if (!destroyed) {
          clearTimeout(timeout);
          setStage('failed');
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    };

    init();

    return () => {
      destroyed = true;
      clearTimeout(timeout);
      if (!reachedReady && viewerInstanceRef.current) {
        viewerInstanceRef.current.finish();
        viewerInstanceRef.current = null;
      }
    };
  }, [stage, urn, authHeader, isShare, shareQS]);

  const handleTranslate = async () => {
    setStage('uploading');
    setError('');
    try {
      const res = await fetch(`/api/aps/translate/${fileId}`, {
        method: 'POST',
        headers: authHeader(),
      });
      const data = await res.json() as { urn?: string; status?: string; error?: string };
      if (!res.ok) throw new Error(data.error ?? 'Translation request failed');
      setUrn(data.urn ?? '');
      if (data.status === 'success') {
        setStage('loading');
      } else {
        setStage('inprogress');
      }
    } catch (err) {
      setStage('failed');
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const progressPct = progress.replace('%', '');

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            <span className="material-symbols-outlined" style={{ fontSize: 17 }}>arrow_back</span>
            Back
          </button>
          <div className={styles.divider} />
          <div className={styles.fileInfo}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#a78bfa' }}>view_in_ar</span>
            <span className={styles.fileName}>{fileInfo?.originalName ?? 'Loading…'}</span>
          </div>
        </div>
        <div className={styles.headerRight}>
          {!isShare && (stage === 'failed' || stage === 'idle' || stage === 'loading') && (
            <button className={styles.actionButton} onClick={() => { setStage('idle'); setUrn(''); setError(''); }}>
              <span className="material-symbols-outlined" style={{ fontSize: 15 }}>refresh</span>
              Retry
            </button>
          )}
          {!isShare && (
            <button className={styles.actionButton}>
              <span className="material-symbols-outlined" style={{ fontSize: 15 }}>share</span>
              Share
            </button>
          )}
        </div>
      </header>

      <div className={styles.viewerBody}>
        <div ref={viewerContainerRef} className={styles.viewerContainer} />

        {stage === 'idle' && (
          <div className={styles.overlay}>
            <div className={styles.overlayIcon}>
              <span className="material-symbols-outlined" style={{ fontSize: 40, color: 'rgba(167,139,250,0.7)' }}>view_in_ar</span>
            </div>
            <p className={styles.overlayTitle}>{isShare ? 'Not Prepared for Viewing' : 'Prepare for Viewing'}</p>
            <p className={styles.overlayText}>
              {isShare
                ? 'This model has not been prepared for online viewing yet. Ask the project owner to open it once, or use the download option on the share page.'
                : 'This 3D BIM file needs to be uploaded to Autodesk Platform Services and translated before it can be viewed. This is a one-time step per file.'}
            </p>
            {!isShare && (
              <button className={styles.translateButton} onClick={handleTranslate}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>rocket_launch</span>
                Upload &amp; Translate
              </button>
            )}
          </div>
        )}

        {stage === 'uploading' && (
          <div className={styles.overlay}>
            <div className={styles.overlayIcon}>
              <span className="material-symbols-outlined" style={{ fontSize: 40, color: 'rgba(99,102,241,0.7)' }}>cloud_upload</span>
            </div>
            <p className={styles.overlayTitle}>Uploading to Autodesk…</p>
            <div className={styles.progressBar}><div className={styles.progressFillIndeterminate} /></div>
          </div>
        )}

        {stage === 'inprogress' && (
          <div className={styles.overlay}>
            <div className={styles.overlayIcon}>
              <span className="material-symbols-outlined" style={{ fontSize: 40, color: 'rgba(245,158,11,0.7)' }}>sync</span>
            </div>
            <p className={styles.overlayTitle}>Translating…</p>
            <p className={styles.overlayText}>
              Autodesk is processing the 3D BIM file. This can take several minutes for large Revit models.
            </p>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{ width: progressPct ? `${progressPct}%` : '100%', animation: progressPct ? 'none' : undefined }}
              />
            </div>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>{progress}</p>
          </div>
        )}

        {stage === 'loading' && (
          <div className={styles.overlay}>
            <div className={styles.overlayIcon}>
              <span className="material-symbols-outlined" style={{ fontSize: 40, color: 'rgba(99,102,241,0.7)' }}>downloading</span>
            </div>
            <p className={styles.overlayTitle}>Opening Viewer…</p>
            <div className={styles.progressBar}><div className={styles.progressFillIndeterminate} /></div>
          </div>
        )}

        {stage === 'misconfigured' && (
          <div className={styles.overlay}>
            <div className={styles.overlayIcon}>
              <span className="material-symbols-outlined" style={{ fontSize: 40, color: 'rgba(251,191,36,0.7)' }}>settings_alert</span>
            </div>
            <p className={styles.overlayTitle}>APS Not Configured</p>
            <p className={styles.overlayText}>
              Set your Autodesk Platform Services credentials in the backend <code>.env</code> file, then restart the server.
            </p>
            <div className={styles.configBox}>
              APS_CLIENT_ID=your_client_id<br />
              APS_CLIENT_SECRET=your_client_secret
            </div>
          </div>
        )}

        {stage === 'failed' && (
          <div className={styles.overlay}>
            <div className={styles.overlayIcon}>
              <span className="material-symbols-outlined" style={{ fontSize: 40, color: 'rgba(239,68,68,0.7)' }}>error</span>
            </div>
            <p className={styles.overlayTitle}>Failed</p>
            <div className={styles.errorBox}>{error}</div>
            {!isShare && (
              <button className={styles.translateButton} onClick={handleTranslate}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>refresh</span>
                Retry
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BIMViewer;
